import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, starPath, vignette, type Painter, type Spot } from '../../art/paint';

const TAU = Math.PI * 2;
const UP = -Math.PI / 2;

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly L: number;
  readonly A1: number;
  readonly A2: number;
  readonly rock: number;
}

interface Shard {
  readonly x: number;
  readonly y: number;
  readonly a: number;
  readonly len: number;
  readonly width: number;
}

function tonesOf(look: Look): Tones {
  const p = look.palette;
  const far = hsl(look.hue, 32, 16);
  return {
    bg: p.bg,
    far,
    ceil: look.ceil,
    ink: p.ink,
    rim: p.rim,
    L: p.incoming.circle,
    A1: p.held,
    A2: p.spot,
    rock: lerpColor(far, p.bg, 0.55),
  };
}

function cave(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.6)));
  g.addColorStop(0.35, rgba(t.bg));
  g.addColorStop(0.75, rgba(lerpColor(t.bg, t.far, 0.4)));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.ink, 0.45)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
}

function spike(p: Painter, x: number, base: number, len: number, width: number, dir: number, lean: number, tone: number, edge: number): void {
  const ctx = p.ctx;
  const half = width / 2;
  const left: Spot[] = [];
  const right: Spot[] = [];
  for (const k of [0.3, 0.62]) {
    const w = half * (1 - k) ** 0.85;
    left.push({ x: x - w + lean * k + p.rng.range(-1, 1) * width * 0.07, y: base + dir * len * k });
    right.push({ x: x + w + lean * k + p.rng.range(-1, 1) * width * 0.07, y: base + dir * len * k });
  }
  ctx.beginPath();
  ctx.moveTo(x - half, base - dir * 6);
  for (const pt of left) ctx.lineTo(pt.x, pt.y);
  ctx.lineTo(x + lean, base + dir * len);
  for (const pt of right.reverse()) ctx.lineTo(pt.x, pt.y);
  ctx.lineTo(x + half, base - dir * 6);
  ctx.closePath();
  ctx.fillStyle = rgba(tone);
  ctx.fill();
  ctx.strokeStyle = rgba(edge, 0.35);
  ctx.lineWidth = 1.2;
  ctx.stroke();
}

function spikes(p: Painter, t: Tones, top: boolean, maxLen: number, keep: number, tone: number): Spot[] {
  const tips: Spot[] = [];
  const base = top ? 0 : p.h;
  const dir = top ? 1 : -1;
  const edge = lerpColor(tone, t.ceil, 0.6);
  let x = -p.unit * 0.04;
  while (x < p.w + p.unit * 0.04) {
    const width = p.unit * p.rng.range(0.07, 0.15);
    const side = Math.min(1, Math.abs(x - p.cx) / (p.w / 2));
    let len = maxLen * p.rng.range(0.35, 1) * (0.5 + 0.6 * side);
    const lean = p.rng.range(-0.15, 0.15) * width;
    while (len > 6 && calmAt(p, x + lean, base + dir * len, keep)) len *= 0.8;
    if (len > 6) {
      spike(p, x, base, len, width, dir, lean, tone, edge);
      tips.push({ x: x + lean, y: base + dir * len });
    }
    x += width * p.rng.range(0.55, 0.95);
  }
  return tips;
}

function wall(p: Painter, t: Tones, left: boolean): void {
  const ctx = p.ctx;
  const out = left ? -12 : p.w + 12;
  const sign = left ? 1 : -1;
  ctx.beginPath();
  ctx.moveTo(out, -12);
  for (let y = -12; y < p.h + 24; y += p.unit * p.rng.range(0.07, 0.13)) {
    const depth = p.unit * p.rng.range(0.025, 0.07) * (0.6 + 0.8 * Math.abs(y - p.cy) / (p.h / 2));
    ctx.lineTo((left ? 0 : p.w) + sign * depth, y);
  }
  ctx.lineTo(out, p.h + 12);
  ctx.closePath();
  ctx.fillStyle = rgba(t.rock);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.4), 0.4);
  ctx.lineWidth = 1.4;
  ctx.stroke();
}

function crystal(p: Painter, t: Tones, c: Shard): void {
  const ctx = p.ctx;
  const ux = Math.cos(c.a);
  const uy = Math.sin(c.a);
  const flip = -uy + ux * 0.01 >= 0 ? 1 : -1;
  const nx = -uy * flip;
  const ny = ux * flip;
  const across = [-0.5, -0.1, 0.22, 0.5];
  const base = (k: number): Spot => ({ x: c.x + nx * c.width * k, y: c.y + ny * c.width * k });
  const sh = (k: number): Spot => ({ x: c.x + ux * c.len * 0.74 + nx * c.width * k, y: c.y + uy * c.len * 0.74 + ny * c.width * k });
  const tip: Spot = { x: c.x + ux * c.len, y: c.y + uy * c.len };
  const faces = [t.ceil, lerpColor(t.ceil, t.far, 0.45), lerpColor(t.far, t.bg, 0.35)];
  for (let i = 0; i < 3; i++) {
    const k0 = across[i]!;
    const k1 = across[i + 1]!;
    const pts = [base(k0), base(k1), sh(k1), tip, sh(k0)];
    ctx.beginPath();
    pts.forEach((pt, j) => {
      if (j === 0) ctx.moveTo(pt.x, pt.y);
      else ctx.lineTo(pt.x, pt.y);
    });
    ctx.closePath();
    ctx.fillStyle = rgba(faces[i]!);
    ctx.fill();
  }
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.25), 0.3);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (const k of [across[1]!, across[2]!]) {
    const b = base(k);
    const s = sh(k);
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(s.x, s.y);
    ctx.lineTo(tip.x, tip.y);
  }
  ctx.stroke();
  const outline = [base(-0.5), sh(-0.5), tip, sh(0.5), base(0.5)];
  ctx.strokeStyle = rgba(t.ink, 0.7);
  ctx.lineWidth = Math.max(1.4, c.width * 0.06);
  ctx.beginPath();
  outline.forEach((pt, j) => {
    if (j === 0) ctx.moveTo(pt.x, pt.y);
    else ctx.lineTo(pt.x, pt.y);
  });
  ctx.stroke();
  const lit = [base(-0.5), sh(-0.5), tip, sh(across[1]!)];
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.55), 0.55);
  ctx.lineWidth = Math.max(1, c.width * 0.035);
  ctx.beginPath();
  lit.forEach((pt, j) => {
    if (j === 0) ctx.moveTo(pt.x, pt.y);
    else ctx.lineTo(pt.x, pt.y);
  });
  ctx.stroke();
}

function mound(p: Painter, t: Tones, at: Spot, r: number): void {
  const ctx = p.ctx;
  const phase = p.rng.float() * TAU;
  ctx.beginPath();
  for (let i = 0; i <= 20; i++) {
    const a = (i / 20) * TAU;
    const k = 1 + 0.14 * Math.sin(3 * a + phase) + 0.07 * Math.sin(5 * a + phase * 1.7);
    const x = at.x + Math.cos(a) * r * k;
    const y = at.y + Math.sin(a) * r * k * 0.6;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = rgba(t.rock);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.6);
  ctx.lineWidth = 1.6;
  ctx.stroke();
}

function cluster(p: Painter, t: Tones, at: Spot, dir: number, size: number, count: number, keep: number): Shard[] {
  const shards: Shard[] = [];
  const sx = Math.cos(dir + Math.PI / 2);
  const sy = Math.sin(dir + Math.PI / 2);
  for (let i = 0; i < count; i++) {
    const spread = (count === 1 ? 0 : i / (count - 1) - 0.5) * 1.5 + p.rng.range(-0.1, 0.1);
    const central = 1 - Math.min(1, Math.abs(spread) / 0.95);
    const a = dir + spread;
    const off = p.rng.range(-0.16, 0.16) * size;
    const x = at.x + sx * off;
    const y = at.y + sy * off;
    let len = size * (0.38 + 0.62 * central) * p.rng.range(0.82, 1.05);
    while (len > 10 && calmAt(p, x + Math.cos(a) * len, y + Math.sin(a) * len, keep)) len *= 0.85;
    if (len > 10) shards.push({ x, y, a, len, width: Math.max(7, len * p.rng.range(0.22, 0.3)) });
  }
  glowDot(p.ctx, at.x + Math.cos(dir) * size * 0.4, at.y + Math.sin(dir) * size * 0.4, size * 1.15, t.L, 0.09);
  shards.sort((a, b) => b.len - a.len);
  for (const c of shards) crystal(p, t, c);
  mound(p, t, at, size * 0.3);
  return shards;
}

function gem(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: number, alpha: number, rim: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = UP + (i / 6) * TAU;
    const px = x + Math.cos(a) * r;
    const py = y + Math.sin(a) * r * 0.8;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = rgba(color, alpha);
  ctx.fill();
  ctx.strokeStyle = rgba(rim, alpha * 0.55);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x - r * 0.6, y - r * 0.15);
  ctx.lineTo(x, y - r * 0.55);
  ctx.lineTo(x + r * 0.6, y - r * 0.15);
  ctx.stroke();
}

function ledge(p: Painter, t: Tones, top: number): Spot[] {
  const ctx = p.ctx;
  const pts: Spot[] = [];
  for (let x = -12; x < p.w + p.unit * 0.16; x += p.unit * p.rng.range(0.08, 0.16)) {
    pts.push({ x, y: top + p.rng.range(-1, 1) * p.h * 0.016 });
  }
  ctx.beginPath();
  ctx.moveTo(-12, p.h + 12);
  for (const pt of pts) ctx.lineTo(pt.x, pt.y);
  ctx.lineTo(p.w + 12, p.h + 12);
  ctx.closePath();
  ctx.fillStyle = rgba(lerpColor(t.rock, t.ink, 0.35));
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.5), 0.5);
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  pts.forEach((pt, i) => {
    if (i === 0) ctx.moveTo(pt.x, pt.y);
    else ctx.lineTo(pt.x, pt.y);
  });
  ctx.stroke();
  const tones = [t.A1, t.A2, t.L];
  const count = Math.round(p.w / (p.unit * 0.11));
  for (let i = 0; i < count; i++) {
    const x = p.rng.float() * p.w;
    const y = top + p.h * p.rng.range(0.012, 0.05);
    if (calmAt(p, x, y, 0.9)) continue;
    gem(ctx, x, y, Math.max(2.5, p.unit * p.rng.range(0.01, 0.02)), p.rng.pick(tones), p.rng.range(0.4, 0.6), t.rim);
  }
  return pts;
}

function glints(p: Painter, t: Tones, shards: readonly Shard[]): void {
  const ctx = p.ctx;
  for (const c of shards.slice(0, 4)) {
    const x = c.x + Math.cos(c.a) * c.len;
    const y = c.y + Math.sin(c.a) * c.len;
    const r = Math.max(5, p.unit * 0.02);
    glowDot(ctx, x, y, r * 2.6, t.A2, 0.14);
    ctx.fillStyle = rgba(lerpColor(t.rim, t.A2, 0.3), 0.55);
    starPath(ctx, x, y, r, 0.2, 4, 0);
    ctx.fill();
  }
}

function hanging(p: Painter, t: Tones, tips: readonly Spot[]): void {
  const picks = tips.filter((tip) => !calmAt(p, tip.x, tip.y + p.unit * 0.08, 0.95));
  const step = Math.max(1, Math.floor(picks.length / 5));
  for (let i = 0; i < picks.length; i += step) {
    const tip = picks[i]!;
    const len = p.unit * p.rng.range(0.04, 0.07);
    crystal(p, t, { x: tip.x, y: tip.y - len * 0.25, a: Math.PI / 2 + p.rng.range(-0.25, 0.25), len, width: len * 0.38 });
  }
}

function veins(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(t.L, 0.16);
  ctx.lineWidth = 1.3;
  ctx.lineCap = 'round';
  for (let v = 0; v < 6; v++) {
    let x = p.rng.chance(0.5) ? p.rng.range(0, p.w * 0.18) : p.rng.range(p.w * 0.82, p.w);
    let y = p.rng.range(p.h * 0.3, p.h);
    if (calmAt(p, x, y, 1)) continue;
    ctx.beginPath();
    ctx.moveTo(x, y);
    let a = p.rng.float() * TAU;
    for (let s = 0; s < 7; s++) {
      a += p.rng.range(-0.9, 0.9);
      const nx = x + Math.cos(a) * p.unit * 0.05;
      const ny = y + Math.sin(a) * p.unit * 0.05;
      if (calmAt(p, nx, ny, 1)) break;
      ctx.lineTo(nx, ny);
      x = nx;
      y = ny;
    }
    ctx.stroke();
  }
}

function motes(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  for (let i = 0; i < 26; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.range(p.h * 0.15, p.h * 0.9);
    if (calmAt(p, x, y, 0.95)) continue;
    const r = p.rng.range(0.8, 1.8);
    glowDot(ctx, x, y, r * 5, t.A1, 0.12);
    ctx.fillStyle = rgba(lerpColor(t.A1, t.rim, 0.4), p.rng.range(0.3, 0.5));
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const portrait = p.w / p.h < 0.8;
  cave(p, t);
  const tips = spikes(p, t, true, portrait ? p.h * 0.3 : p.unit * 0.6, 0.95, t.far);
  spikes(p, t, false, portrait ? p.h * 0.16 : p.unit * 0.42, 0.95, lerpColor(t.far, t.bg, 0.3));
  wall(p, t, true);
  wall(p, t, false);
  const shards: Shard[] = [];
  if (portrait) {
    const size = Math.min(p.h * 0.21, p.w * 0.46);
    shards.push(...cluster(p, t, { x: p.w * 0.74, y: p.h * 0.9 }, UP - 0.22, size, 7, 1));
    shards.push(...cluster(p, t, { x: p.w * 0.07, y: p.h * 0.88 }, UP + 0.32, Math.min(p.h * 0.13, p.w * 0.28), 4, 1));
  } else {
    const size = Math.min(p.h * 0.42, p.unit * 0.95);
    shards.push(...cluster(p, t, { x: p.w * 0.05, y: p.h * 0.64 }, UP + 0.55, size, 7, 1));
    shards.push(...cluster(p, t, { x: p.w * 0.95, y: p.h * 0.8 }, UP - 0.5, size * 0.62, 5, 1));
  }
  ledge(p, t, p.h * (portrait ? 0.935 : 0.9));
  shards.sort((a, b) => b.len - a.len);
  if (p.level >= 2) glints(p, t, shards);
  if (p.level >= 3) hanging(p, t, tips);
  if (p.level >= 4) veins(p, t);
  if (p.level >= 5) motes(p, t);
  calmCenter(p, t.bg, 0.6, 1.15);
  grain(p, 0.05, true);
  vignette(p, t.ink, 0.45);
}
